import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography,
} from '@mui/material';
import * as CompanyAnnualService from '@/services/accountService/companyAnnualService';
import type { CompanyAnnualSelectItem } from '@/services/accountService/companyAnnualService';
import CompanyAnnualDetail from '@/components/company-info/company-annual/CompanyAnnualDetail';
import type { CompanyAnnualDetailHandle } from '@/components/company-info/company-annual/CompanyAnnualDetail';
import { fetchGlSheetPage } from '@/services/accountService/glInvoiceService';
import { useGlInvoiceCreateCtx } from '@/pages/components/account/gl-invoice/create-stepper/context';
import { computeYearOfAssessment } from '@/utils/dataUtils';
import { buildTaxFileNoFromGlImport } from '@/utils/glImportHelpers';

const PANEL_HEIGHT = 'calc(100vh - 300px)';

export default function StepBAnnual() {
    const { state, dispatch } = useGlInvoiceCreateCtx();

    const companyId = state.companyId;
    const selectedAnnual = state.selectedAnnual;

    const [annuals, setAnnuals] = useState<CompanyAnnualSelectItem[]>([]);
    const [annualLoading, setAnnualLoading] = useState(false);
    const [annualError, setAnnualError] = useState<string | null>(null);
    const [annualInput, setAnnualInput] = useState('');
    const [showCreateAnnual, setShowCreateAnnual] = useState(false);
    const [inlineCreateOpen, setInlineCreateOpen] = useState(false);

    const [invoiceCheckLoading, setInvoiceCheckLoading] = useState(false);
    const [existingInvoiceCount, setExistingInvoiceCount] = useState<number | null>(null);
    const [checkError, setCheckError] = useState<string | null>(null);

    const annualsLoadedRef = useRef(false);
    const contentMatchedRef = useRef(false);
    const annualDetailRef = useRef<CompanyAnnualDetailHandle>(null);

    const loadAnnuals = useCallback(async () => {
        if (!companyId) { setAnnuals([]); return; }
        setAnnualLoading(true);
        setAnnualError(null);
        try {
            const list = await CompanyAnnualService.querySelectionList(companyId);
            setAnnuals(list || []);
            annualsLoadedRef.current = true;
        } catch (e: any) {
            setAnnualError(e?.message || 'Failed to load annuals');
        } finally {
            setAnnualLoading(false);
        }
    }, [companyId]);

    useEffect(() => { loadAnnuals(); }, [loadAnnuals]);

    useEffect(() => {
        setShowCreateAnnual(!selectedAnnual && !inlineCreateOpen);
    }, [selectedAnnual, inlineCreateOpen]);

    // Auto-match annual by yearOfAssessment from imported contentData (参照 FsAnnual StepBCaBasicInfo)
    useEffect(() => {
        if (contentMatchedRef.current) return;
        const cd = state.contentData;
        if (!cd || !cd.periodTo) return;
        if (!companyId || annualLoading || !annualsLoadedRef.current) return;

        contentMatchedRef.current = true;

        // Match by yearOfAssessment (computed from periodTo)
        const excelYoa = computeYearOfAssessment(cd.periodTo);
        const matched = excelYoa
            ? annuals.find((a) => a.yearOfAssessment === excelYoa)
            : null;

        if (matched) {
            // Selection mode: select the matched annual
            dispatch({ type: 'set_annual', payload: matched });
            dispatch({ type: 'set_is_creating_annual', payload: false });
        } else {
            // Creation mode: no existing annual matches, open inline create
            setInlineCreateOpen(true);
            dispatch({ type: 'set_annual', payload: null });
            dispatch({ type: 'set_is_creating_annual', payload: true });
        }
    }, [state.contentData, annuals, annualLoading, companyId, dispatch]);

    // Build initialData for CompanyAnnualDetail (Excel import + company BR fallback)
    const annualInitialData = useMemo(() => {
        if (!state.contentData) return undefined;
        const cd = state.contentData;
        const taxFileNo = buildTaxFileNoFromGlImport(cd)
            ?? buildTaxFileNoFromGlImport({ br: (state.selectedCompany as any)?.br });
        return {
            periodFrom: cd.periodFrom,
            periodTo: cd.periodTo,
            lastYearPeriodFrom: cd.lastYearPeriodFrom,
            lastYearPeriodTo: cd.lastYearPeriodTo,
            presentationCurrency: cd.presentationCurrency,
            functionalCurrency: cd.functionalCurrency,
            limitByGuarantee: cd.limitByGuarantee,
            qualifyOpinion: cd.qualifyOpinion,
            firstAudit: cd.firstAudit,
            ppe: cd.ppe,
            relatedPartyTransactions: cd.relatedPartyTransactions,
            rentalCommitment: cd.rentalCommitment,
            holdingCo: cd.holdingCo,
            nameOfImmediate: cd.nameOfImmediate,
            nameOfUltimate: cd.nameOfUltimate,
            allotmentOfShares: cd.allotmentOfShares,
            noOfShares: cd.noOfShares,
            pricePerShare: cd.pricePerShare,
            priceCurrency: cd.priceCurrency,
            dividendPaid: cd.dividendPaid,
            dividendCurrency: cd.dividendCurrency,
            amountPerEachShare: cd.amountPerEachShare,
            totalDividendAmount: cd.totalDividendAmount,
            cessationDuringThisPeriod: cd.cessation,
            changeOfAddress: cd.changeOfAddress,
            lastAddress: cd.lastAddress,
            isChangeName: cd.isChangeName,
            oldCompanyName: cd.oldCompanyName,
            changeEffectiveDate: cd.changeEffectiveDate,
            changeOfFinancialYearEnd: cd.changeOfFinancialYearEnd,
            dateOfBoardResolution: cd.dateOfBoardResolution,
            reasonToChange: cd.reasonToChange,
            formerAuditor: cd.formerAuditor,
            addressOfFormerAuditor: cd.addressOfFormerAuditor,
            twoTierTaxThisYear: cd.twoTierTaxThisYear,
            ...(taxFileNo ? { taxFileNo } : {}),
        };
    }, [state.contentData, state.selectedCompany]);

    // Build preview data from contentData
    const previewData = useMemo(() => {
        const cd = state.contentData;
        if (!cd) return null;
        return {
            periodFrom: cd.periodFrom,
            periodTo: cd.periodTo,
            yearOfAssessment: computeYearOfAssessment(cd.periodTo),
            presentationCurrency: cd.presentationCurrency,
            functionalCurrency: cd.functionalCurrency,
            qualifyOpinion: cd.qualifyOpinion,
            firstAudit: cd.firstAudit,
        };
    }, [state.contentData]);

    const checkInvoiceCount = useCallback(async (annualId: number) => {
        if (!companyId) return;
        setInvoiceCheckLoading(true);
        setCheckError(null);
        try {
            const res = await fetchGlSheetPage({
                companyId, annualId, pageNumber: 1, pageSize: 1,
            });
            setExistingInvoiceCount(res.totalRow ?? 0);
        } catch (e: any) {
            setCheckError(e?.message || 'Failed to check invoice data');
        } finally {
            setInvoiceCheckLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        if (selectedAnnual?.annualId) {
            checkInvoiceCount(selectedAnnual.annualId);
        } else {
            setExistingInvoiceCount(null);
        }
    }, [selectedAnnual, checkInvoiceCount]);

    const handleAnnualCreated = useCallback(async () => {
        setInlineCreateOpen(false);
        dispatch({ type: 'set_is_creating_annual', payload: false });
        const list = await (async () => {
            setAnnualLoading(true);
            try {
                const l = await CompanyAnnualService.querySelectionList(companyId!);
                setAnnuals(l || []);
                return l || [];
            } catch { return []; } finally { setAnnualLoading(false); }
        })();
        if (list.length > 0) {
            const newest = list.reduce((a, b) => b.annualId > a.annualId ? b : a);
            dispatch({ type: 'set_annual', payload: newest });
        }
    }, [companyId, dispatch]);

    // Determine if CompanyAnnualDetail is shown (for beforeNext registration)
    const showsAnnualDetail = !!(selectedAnnual && !inlineCreateOpen) || !!(inlineCreateOpen && companyId);

    // Register beforeNext: when CompanyAnnualDetail is visible, trigger its submit via ref
    useEffect(() => {
        if (showsAnnualDetail) {
            const handler = async (): Promise<boolean> => {
                if (!annualDetailRef.current) return true;
                const ok = await annualDetailRef.current.submit();
                if (!ok) {
                    const msg = annualDetailRef.current.getValidationMessage()
                        ?? 'Please complete required annual fields before continuing.';
                    dispatch({ type: 'set_step_error', payload: msg });
                    return false;
                }
                dispatch({ type: 'set_step_error', payload: null });
                if (ok && inlineCreateOpen) {
                    await handleAnnualCreated();
                } else if (ok && selectedAnnual) {
                    loadAnnuals();
                }
                return ok;
            };
            dispatch({ type: 'set_before_next', payload: handler });
        } else {
            dispatch({ type: 'set_before_next', payload: undefined });
        }
        return () => {
            dispatch({ type: 'set_before_next', payload: undefined });
        };
    }, [showsAnnualDetail, inlineCreateOpen, selectedAnnual, dispatch, handleAnnualCreated, loadAnnuals]);

    const getLabel = (a: CompanyAnnualSelectItem) =>
        `${a.yearOfAssessment ?? '-'} (${a.periodFrom ?? '?'} ~ ${a.periodTo ?? '?'})`;

    return (
        <Box sx={{ height: PANEL_HEIGHT, overflow: 'auto' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>Select Annual</Typography>
            {annualError && <Alert severity="error" sx={{ mb: 1 }}>{annualError}</Alert>}
            {checkError && <Alert severity="warning" sx={{ mb: 1 }}>{checkError}</Alert>}

            <Autocomplete
                value={selectedAnnual}
                disabled={inlineCreateOpen}
                onChange={(_, v) => {
                    dispatch({ type: 'set_annual', payload: v });
                    setExistingInvoiceCount(null);
                }}
                options={annuals}
                loading={annualLoading}
                getOptionLabel={getLabel}
                isOptionEqualToValue={(opt, val) => opt.annualId === val.annualId}
                inputValue={annualInput}
                onInputChange={(_, v) => setAnnualInput(v)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Annual"
                        placeholder="Select an annual period"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {annualLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                    {showCreateAnnual && !inlineCreateOpen && (
                                        <Button
                                            size="small"
                                            sx={{ ml: 0.5 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setInlineCreateOpen(true);
                                                dispatch({ type: 'set_annual', payload: null });
                                                dispatch({ type: 'set_is_creating_annual', payload: true });
                                            }}
                                        >
                                            Create Annual
                                        </Button>
                                    )}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {selectedAnnual && !inlineCreateOpen && (
                <Box mt={2}>
                    {invoiceCheckLoading && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="text.secondary">Checking invoice data...</Typography>
                        </Stack>
                    )}
                    {!invoiceCheckLoading && existingInvoiceCount !== null && (
                        <Alert
                            severity={existingInvoiceCount > 0 ? 'info' : 'success'}
                            sx={{ mt: 1 }}
                        >
                            {existingInvoiceCount > 0
                                ? `This annual has ${existingInvoiceCount} existing invoice record(s). Proceeding will load them for editing.`
                                : 'No invoice data for this annual. You can start adding new entries.'}
                        </Alert>
                    )}

                    {/* When contentData exists (excel uploaded), render CompanyAnnualDetail for editing */}
                    {state.contentData ? (
                        <Box mt={2}>
                            <Alert severity="info" sx={{ mb: 1 }}>
                                Matched existing annual. Review and update fields with imported data below.
                            </Alert>
                            <CompanyAnnualDetail
                                ref={annualDetailRef}
                                hideActions
                                open
                                renderMode="content"
                                annualId={selectedAnnual.annualId}
                                isAdd={false}
                                companyId={companyId ?? undefined}
                                lockCompanySelection
                                createGuardFlow="account"
                                initialData={annualInitialData}
                                onClose={(payload) => {
                                    if (payload?.message?.includes('Update Successful')) {
                                        loadAnnuals();
                                    }
                                }}
                            />
                        </Box>
                    ) : (
                        /* No contentData: render CompanyAnnualDetail for viewing/editing */
                        <Box mt={2}>
                            <CompanyAnnualDetail
                                ref={annualDetailRef}
                                hideActions
                                open
                                renderMode="content"
                                annualId={selectedAnnual.annualId}
                                isAdd={false}
                                companyId={companyId ?? undefined}
                                lockCompanySelection
                                createGuardFlow="account"
                                onClose={(payload) => {
                                    if (payload?.message?.includes('Update Successful')) {
                                        loadAnnuals();
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            )}

            {inlineCreateOpen && companyId && (
                <Box mt={2}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                        {state.contentData
                            ? 'Creating a new annual with imported Company Info data. Review and adjust fields below.'
                            : 'Creating a new annual. Fill in the required fields below.'}
                    </Alert>
                    <CompanyAnnualDetail
                        ref={annualDetailRef}
                        hideActions
                        open
                        renderMode="content"
                        isAdd
                        companyId={companyId}
                        lockCompanySelection
                        createGuardFlow="account"
                        createFlowType={2}
                        initialData={annualInitialData}
                        onClose={async (payload) => {
                            if (payload?.message?.includes('Create Successful')) {
                                // handled by beforeNext
                            } else {
                                setInlineCreateOpen(false);
                                dispatch({ type: 'set_is_creating_annual', payload: false });
                                dispatch({ type: 'set_annual', payload: null });
                            }
                        }}
                    />
                </Box>
            )}

            {state.isCreatingAnnual && !inlineCreateOpen && state.annualId && state.annualId > 0 && (
                <Box mt={2}>
                    {invoiceCheckLoading && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={14} />
                            <Typography variant="caption" color="text.secondary">Checking invoice data...</Typography>
                        </Stack>
                    )}
                    {!invoiceCheckLoading && existingInvoiceCount !== null && existingInvoiceCount > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            This annual already has {existingInvoiceCount} invoice record(s). Existing data will be loaded for editing.
                        </Alert>
                    )}
                    {!invoiceCheckLoading && existingInvoiceCount === 0 && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            New annual created successfully. You can start adding invoice entries.
                        </Alert>
                    )}
                </Box>
            )}
        </Box>
    );
}
